import clsx from 'clsx';
import Icon from 'components/atoms/Icon';
import ListWithIcon from 'components/molecules/ListWithIcon/ListWithIcon';
import { ProjectStatus } from 'generated/graphql';
import { STATUS_LABEL_MAP } from 'views/Project/components/ProjectStatusDropdown/ProjectStatusDropdown.constants';
import { Button, IconType, Modal } from '@spotted-zebra-uk/ui-components';
import styles from './ArchiveProjectModal.module.scss';

const ArchiveProjectModal = ({
  onSubmit,
  onCancel,
  projectName,
  loading,
  isArchived,
  currentProjectStatus,
}: {
  onSubmit: () => void;
  onCancel: () => void;
  projectName: string;
  loading: boolean;
  isArchived: boolean;
  currentProjectStatus: ProjectStatus;
}) => {
  const actionLabel = isArchived ? 'Unarchive' : 'Archive';

  return (
    <Modal
      header={`${actionLabel} ${projectName}`}
      headerIconType={isArchived ? undefined : IconType.WARNING}
      onClose={onCancel}
      isOpen={true}
      className={styles.modalContainer}
    >
      {isArchived ? (
        <div className={styles.content}>
          <p className={styles.description}>
            Unarchiving will reinstate the role to{' '}
            {STATUS_LABEL_MAP[currentProjectStatus]}. Depending on status, users
            will regain all access to the role.
          </p>
        </div>
      ) : (
        <div className={styles.content}>
          <div className={styles.descriptionContainer}>
            <p className={styles.description}>
              Archiving will remove this role from client users' view. Use this
              option for roles created in error, duplicates, demos, or tests
              that don't contain important data.
            </p>
            <p className={styles.description}>
              Once archived, the role will no longer be accessible to clients or
              used in reporting. Roles can be unarchived and restored to{' '}
              {STATUS_LABEL_MAP[currentProjectStatus]}.
            </p>
          </div>
          <div className={styles.contentList}>
            <ListWithIcon
              icon={
                <div className={clsx(styles.iconContainer, styles.lockIcon)}>
                  <Icon icon="lock" />
                </div>
              }
              title="Locked when archived"
              listItems={[
                'Access to the role',
                'Access to role and candidate data',
              ]}
            />
            <ListWithIcon
              icon={
                <div className={clsx(styles.iconContainer, styles.checkIcon)}>
                  <Icon icon="check" />
                </div>
              }
              title="Available when archived"
              listItems={['All other settings', 'Unarchive via Admin']}
            />
          </div>
        </div>
      )}
      <div className={styles.actions}>
        <Button size="medium" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="medium"
          variant={isArchived ? 'primary' : 'negativePrimary'}
          onClick={onSubmit}
          loading={loading}
        >
          {actionLabel} role
        </Button>
      </div>
    </Modal>
  );
};

export default ArchiveProjectModal;
